import { anyObject, anyReallyAny } from "./any.tools";

/** get data in cache if present, loadData otherwise */
export function loadIfNeeded<CacheKey, DataType>(
  cacheMap: Map<CacheKey, DataType>,
  cacheKey: CacheKey,
  loadData: () => DataType
): DataType {
  if (cacheMap.has(cacheKey)) {
    return cacheMap.get(cacheKey)!; // We have tested that the line exists
  } else {
    const data = loadData();
    cacheMap.set(cacheKey, data);
    return data;
  }
}


/**
 * clear all on object
 */
export function destroyObject(obj: anyObject): void {
  if (obj == null) { return; }
  for (const k in obj) {
    delete obj[k];
  }
}

export interface CacheOwner {
  cacheData: Map<string, unknown>;
}

/** Cache owner with a loadIfNeeded method (for cache data not directly returned by a method) */
export class DataWithCache implements CacheOwner {

  public cacheData: Map<string, unknown> = new Map();

  /** get data in cache if present, loadData otherwise */
  public loadIfNeeded<T>(cacheKey: anyReallyAny, loadData: () => T): T {
    return loadIfNeeded(this.cacheData, cacheKey, loadData) as T;
  }
}

type ParamKeyGetter = (param: anyReallyAny) => string | undefined;

/**
 * Build method cache key from propetyKey and argsKey
 * @returns  cache method string key
 */
export function buildMethodCacheKey<T = string>(propertyKey: T, argsKey: string): string {
  return "@Cached_method_" + propertyKey + argsKey;
}
/**
 * Build get cache key from propetyKey 
 * @returns  cache get string key
 */
export function buildGetCacheKey<T = string>(propertyKey: T): string {
  return "@Cached_get_" + propertyKey;
}

/** 
 * Method or get Accessor decorator that wraps the method with a loadIfNeeded call in order to keep return value in cacheData
 * @param p
 * @param p.paramsKeyGetters define key getter foreach param, if undefined returned, param will be stringified to generated key
 */
export function Cached(p?: { paramsKeyGetters?: ParamKeyGetter[] }) {
  return function (target: CacheOwner, propertyKey: string, descriptor: PropertyDescriptor): PropertyDescriptor {
    if (descriptor.get) {
      // get accessor
      const originalGet = descriptor.get;
      descriptor.get = function () {
        const that = this as CacheOwner;
        if (that.cacheData == null) {
          that.cacheData = new Map<string, unknown>();
        }
        const cacheKey = buildGetCacheKey(propertyKey);
        return loadIfNeeded(that.cacheData, cacheKey, () => {
          return originalGet.apply(this);
        });
      };
    } else {
      // Method
      const originalValue = descriptor.value;
      descriptor.value = function (...args: anyReallyAny[]) {
        const that = this as CacheOwner;
        if (that.cacheData == null) {
          that.cacheData = new Map<string, unknown>();
        }

        // Build key, each key is separated by an underscore ("_")
        let argsKey = "";
        args.forEach((arg, index) => {
          const keyGetter = p?.paramsKeyGetters?.[index];
          argsKey += "_" + (keyGetter?.(arg) ?? JSON.stringify(arg));
        });

        const cacheKey = buildMethodCacheKey(propertyKey, argsKey);
        return loadIfNeeded(that.cacheData, cacheKey, () => {
          return originalValue.apply(this, args);
        });
      };
    }

    return descriptor;
  };
}
